/* Feature Hotspots 🛠️ — sections/custom-feature-hotspots.liquid
 * One feature open at a time. Choosing a point on the image or a title in the list opens that
 * feature and highlights its point. If the feature has a detail image, it swipes in over the main
 * image (a feature without one swipes back to the main image). Choosing the open feature again,
 * or the close button, closes it: nothing selected, main image. The page loads that way too.
 * In the theme editor, selecting a block opens it (and shows its detail image). */

if (!customElements.get('feature-hotspots')) {
  // How long a layer's swipe takes, read from its CSS transition (so reduced motion etc. just work)
  const swipeMs = (el) => parseFloat(getComputedStyle(el).transitionDuration) * 1000 || 0;

  class FeatureHotspots extends HTMLElement {
    // connected/disconnected also run when the theme editor re-renders the section.
    connectedCallback() {
      this.points = [...this.querySelectorAll('.hotspots__point')];
      this.items = [...this.querySelectorAll('.hotspots__item')];
      this.details = new Map([...this.querySelectorAll('.hotspots__detail')].map((d) => [Number(d.dataset.index), d]));
      this.close = this.querySelector('.hotspots__close');
      this.shown = null; // the detail layer currently covering the main image

      this.onClick = (e) => {
        if (e.target.closest('.hotspots__close')) return this.open(null);
        const trigger = e.target.closest('.hotspots__point, .hotspots__item-head');
        if (!trigger) return;
        const index = Number(trigger.dataset.index ?? trigger.closest('.hotspots__item').dataset.index);
        // Choosing the open feature again closes it
        this.open(index === this.index ? null : index);
      };
      this.addEventListener('click', this.onClick);
      this.onBlockSelect = (e) => {
        const i = this.items.indexOf(e.target);
        if (i >= 0) this.open(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.index = null; // nothing open until the visitor chooses a feature

      // Detail images are lazy in the HTML (no download if nobody scrolls this far). As the section
      // approaches, fetch and decode them all so the first swipe to each shows the image straight away.
      if (this.details.size) {
        this.preloader = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          this.preloader.disconnect();
          this.details.forEach((layer) => {
            const img = layer.querySelector('img');
            if (!img) return;
            img.loading = 'eager';
            img.decode?.().catch(() => {}); // decode ahead of time; ignore images that fail
          });
        }, { rootMargin: '800px 0px' });
        this.preloader.observe(this);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.underTimer);
      this.preloader?.disconnect();
    }

    // Open one feature (index) and show its detail image, or close everything (null).
    open(index) {
      this.index = index;
      this.items.forEach((item, i) => {
        const active = i === index;
        item.classList.toggle('is-active', active);
        item.querySelector('.hotspots__item-head').setAttribute('aria-expanded', active);
      });
      this.points.forEach((point, i) => point.classList.toggle('is-active', i === index));
      this.showDetail(this.details.has(index) ? index : null);
    }

    // Swipe a feature's detail image in (index), or back to the main image (null).
    showDetail(index) {
      const next = index === null ? null : this.details.get(index);
      const prev = this.shown;
      if (next === prev) return;
      clearTimeout(this.underTimer);

      if (next) {
        // The new layer swipes in on top; the old one (if any) stays put underneath until it's
        // covered, then resets out of sight.
        next.style.zIndex = 2;
        next.classList.add('is-shown');
        if (prev) {
          prev.classList.add('is-under');
          prev.style.zIndex = 1;
          this.underTimer = setTimeout(() => {
            prev.style.transition = 'none'; // reset instantly, not with a swipe out
            prev.classList.remove('is-shown', 'is-under');
            prev.style.zIndex = '';
            prev.offsetWidth; // apply that before the transition comes back
            prev.style.transition = '';
          }, swipeMs(next));
        }
      } else if (prev) {
        prev.classList.remove('is-shown'); // swipes back out to the left
      }

      this.shown = next;
      this.classList.toggle('is-detail', !!next);
      this.close.hidden = !next;
    }
  }

  customElements.define('feature-hotspots', FeatureHotspots);
}
